import * as React from "react";
import Section from "../layout/Section";
import { useStaticQuery, graphql } from "gatsby";
import { twMerge } from "tailwind-merge";

interface ClientsSectionsProps {
  classNames?: string;
  border?: boolean;
}

const ClientsSections: React.FunctionComponent<ClientsSectionsProps> = ({
  classNames,
  border = true,
}) => {
  const clients: Record<string, { name: string; link: string; image: string }> =
    useStaticQuery(graphql`
      query ClientsQuery {
        bepower: clientsJson(jsonId: { eq: "bepower" }) {
          name
          link
          image
        }
        sailsquare: clientsJson(jsonId: { eq: "sailsquare" }) {
          name
          link
          image
        }
        memorabid: clientsJson(jsonId: { eq: "memorabid" }) {
          name
          link
          image
        }
        hellobarrio: clientsJson(jsonId: { eq: "hellobarrio" }) {
          name
          link
          image
        }
        limo: clientsJson(jsonId: { eq: "limo" }) {
          name
          link
          image
        }
        mechit: clientsJson(jsonId: { eq: "mechit" }) {
          name
          link
          image
        }
        backwelltech: clientsJson(jsonId: { eq: "backwelltech" }) {
          name
          link
          image
        }
        bookingmyhotel: clientsJson(jsonId: { eq: "bookingmyhotel" }) {
          name
          link
          image
        }
        ictc: clientsJson(jsonId: { eq: "ictc" }) {
          name
          link
          image
        }
      }
    `);

  // Build ordered array from query result
  const clientsArray = Object.keys(clients).map((k) => clients[k]);

  return (
    <div
      className={twMerge(
        "py-12  bg-white dark:bg-gray-950 z-10  border-gray-100 dark:border-gray-800",
        border && "border-y",
        classNames,
      )}
    >
      <div className="overflow-hidden w-full relative  mx-auto select-none">
        <div className="absolute left-0 top-0 h-full w-20 z-10 pointer-events-none bg-gradient-to-r from-white dark:from-gray-950 to-transparent" />
        <div
          className="marquee-inner flex will-change-transform min-w-[200%]"
          style={{ animationDuration: "25s" }}
        >
          <div className="flex">
            {[...clientsArray, ...clientsArray].map((client, index) => (
              <ClientImage
                key={`a-${index}`}
                {...client}
                className="inline-flex items-center justify-center px-10 flex-shrink-0"
              />
            ))}
          </div>
        </div>
        <div className="absolute right-0 top-0 h-full w-20 md:w-40 z-10 pointer-events-none bg-gradient-to-l from-white dark:from-gray-950 to-transparent" />
      </div>
    </div>
  );
};

// Monochrome dark logos that would vanish on the dark-mode background.
// Class names must be spelled out for Tailwind to generate them.
const darkLogoClassNames: Record<string, string> = {
  "mechit.png": "dark:invert",
  "hellobarrio.svg": "dark:brightness-0 dark:invert",
  // Dark wordmarks with a colored accent: invert lightness, keep the hue.
  "memorabid.svg": "dark:invert dark:hue-rotate-180",
  "backwelltech.svg": "dark:invert dark:hue-rotate-180",
  "bookingmyhotel.avif": "dark:invert dark:hue-rotate-180",
};

export const clientLogoDarkClassName = (image: string) =>
  darkLogoClassNames[image] ?? "";

export const ClientImage = (props: {
  name: string;
  link: string;
  image: string;
  className?: string;
  size?: "sm" | "md";
}) => (
  <a
    href={props.link}
    target="_blank"
    rel="noopener noreferrer"
    className={`${props.className || ""}`}
  >
    <img
      src={require(`../../assets/images/clients/${props.image}`).default}
      alt={props.name}
      className={twMerge(
        "block object-contain shrink-0",
        props.size === "sm" ? "h-6 xl:h-7" : "h-8 md:h-10 ",
        clientLogoDarkClassName(props.image),
      )}
    />
  </a>
);

export default ClientsSections;
